import axios from "axios";
import { getCookie } from "../../utils/cookies";
import {
  FETCH_EMAIL_STATS,
  FETCH_EMAIL_EVENTS,
  FETCH_EMAIL_TIMELINE,
  FETCH_EMAIL_SUBJECTS,
  FETCH_CAMPAIGN_STATS,
  FETCH_SEQUENCE_STATS,
  FETCH_SEQUENCE_USERS,
  EMAIL_ANALYTICS_ERROR,
  EMAIL_ANALYTICS_LOADING,
  CLEAR_EMAIL_ANALYTICS,
} from "./types";

const API_URL = process.env.REACT_APP_GOLANG_API;

// Helper to set auth header
const setAuthHeader = () => {
  const token = getCookie("_session");
  if (token) {
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${JSON.parse(token)}`;
    } catch {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }
};

// Fetch email statistics
export const fetchEmailStats = (startDate, endDate) => async (dispatch) => {
  try {
    dispatch({ type: EMAIL_ANALYTICS_LOADING, payload: { stats: true } });
    setAuthHeader();

    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const response = await axios.get(
      `${API_URL}/api/v2/email-analytics/stats?${params.toString()}`
    );

    dispatch({
      type: FETCH_EMAIL_STATS,
      payload: response.data,
    });
  } catch (error) {
    dispatch({
      type: EMAIL_ANALYTICS_ERROR,
      payload: error.response?.data?.message || error.message,
    });
  }
};

// Fetch email events with pagination and filters
export const fetchEmailEvents = (filters = {}) => async (dispatch) => {
  try {
    dispatch({ type: EMAIL_ANALYTICS_LOADING, payload: { events: true } });
    setAuthHeader();

    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.eventType) params.append("eventType", filters.eventType);
    if (filters.subject) params.append("subject", filters.subject);
    if (filters.email) params.append("email", filters.email);
    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit || 50);

    const response = await axios.get(
      `${API_URL}/api/v2/email-analytics/events?${params.toString()}`
    );

    dispatch({
      type: FETCH_EMAIL_EVENTS,
      payload: response.data,
    });
  } catch (error) {
    dispatch({
      type: EMAIL_ANALYTICS_ERROR,
      payload: error.response?.data?.message || error.message,
    });
  }
};

// Fetch timeline data for charts
export const fetchEmailTimeline = (startDate, endDate, groupBy = "day") => async (dispatch) => {
  try {
    dispatch({ type: EMAIL_ANALYTICS_LOADING, payload: { timeline: true } });
    setAuthHeader();

    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    params.append("groupBy", groupBy);

    const response = await axios.get(
      `${API_URL}/api/v2/email-analytics/timeline?${params.toString()}`
    );

    dispatch({
      type: FETCH_EMAIL_TIMELINE,
      payload: response.data,
    });
  } catch (error) {
    dispatch({
      type: EMAIL_ANALYTICS_ERROR,
      payload: error.response?.data?.message || error.message,
    });
  }
};

// Fetch unique subjects for filter dropdown
export const fetchEmailSubjects = () => async (dispatch) => {
  try {
    setAuthHeader();

    const response = await axios.get(
      `${API_URL}/api/v2/email-analytics/subjects`
    );

    dispatch({
      type: FETCH_EMAIL_SUBJECTS,
      payload: response.data.subjects,
    });
  } catch (error) {
    dispatch({
      type: EMAIL_ANALYTICS_ERROR,
      payload: error.response?.data?.message || error.message,
    });
  }
};

// Export CSV - returns URL for download
export const exportEmailCSV = async (filters = {}) => {
  setAuthHeader();

  const params = new URLSearchParams();
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.eventType) params.append("eventType", filters.eventType);

  const response = await axios.get(
    `${API_URL}/api/v2/email-analytics/export?${params.toString()}`,
    { responseType: "blob" }
  );

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `email_events_${new Date().toISOString().split("T")[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Fetch email sequence (onboarding) statistics
export const fetchSequenceStats = () => async (dispatch) => {
  try {
    dispatch({ type: EMAIL_ANALYTICS_LOADING, payload: { sequence: true } });
    setAuthHeader();

    const response = await axios.get(
      `${API_URL}/api/v2/email-analytics/sequence/stats`
    );

    dispatch({
      type: FETCH_SEQUENCE_STATS,
      payload: response.data,
    });
  } catch (error) {
    dispatch({
      type: EMAIL_ANALYTICS_ERROR,
      payload: error.response?.data?.message || error.message,
    });
  }
};

// Fetch email sequence users with filters
export const fetchSequenceUsers = (filters = {}) => async (dispatch) => {
  try {
    dispatch({ type: EMAIL_ANALYTICS_LOADING, payload: { sequenceUsers: true } });
    setAuthHeader();

    const params = new URLSearchParams();
    if (filters.day) params.append("day", filters.day);
    // Only send completed filter when it's explicitly "true" or "false" string
    if (filters.completed === "true" || filters.completed === "false") {
      params.append("completed", filters.completed);
    }
    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit || 50);

    const response = await axios.get(
      `${API_URL}/api/v2/email-analytics/sequence/users?${params.toString()}`
    );

    dispatch({
      type: FETCH_SEQUENCE_USERS,
      payload: response.data,
    });
  } catch (error) {
    dispatch({
      type: EMAIL_ANALYTICS_ERROR,
      payload: error.response?.data?.message || error.message,
    });
  }
};

// Fetch campaign/subject statistics (funnel data)
export const fetchCampaignStats = (startDate, endDate, limit = 20) => async (dispatch) => {
  try {
    dispatch({ type: EMAIL_ANALYTICS_LOADING, payload: { campaigns: true } });
    setAuthHeader();

    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    params.append("limit", limit);

    const response = await axios.get(
      `${API_URL}/api/v2/email-analytics/subjects/stats?${params.toString()}`
    );

    dispatch({
      type: FETCH_CAMPAIGN_STATS,
      payload: response.data,
    });
  } catch (error) {
    dispatch({
      type: EMAIL_ANALYTICS_ERROR,
      payload: error.response?.data?.message || error.message,
    });
  }
};

// Clear email analytics state
export const clearEmailAnalytics = () => ({
  type: CLEAR_EMAIL_ANALYTICS,
});
